import Head from "next/head";
import Image from "next/image";
import Link from "next/link";

import { ArrowRightIcon } from "lucide-react";

import { formatImageUrl } from "@/components/blog/Util";
import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import { getAllPages } from "@/lib/contentful";
import { cn } from "@/lib/utils";
import { CaseStudy, HeaderSubheaderComponent } from "@/pages/case-studies/[slug]";
import { clashGrotesk, jakartaSans } from "@/styles/fonts";

const CaseStudies = ({ caseStudies }: { caseStudies: CaseStudy[] }) => {
  const baseUrl = process.env.NEXT_PUBLIC_API_HOST || "https://rebolthq.com";
  const canonicalUrl = `${baseUrl}/case-studies`;
  return (
    <>
      <Head>
        <title>Case Studies | Rebolt</title>
        <meta
          name="description"
          content=" Learn more about how home service pros are using Rebolt to grow their business."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          property="og:image"
          content="https://api.screenshotone.com/take?access_key=cQWOH7FyQTatDw&url=https://rebolthq.com/case-studies&full_page=false&viewport_width=1200&viewport_height=850&device_scale_factor=1&format=jpg&image_quality=80&cache=true&cache_ttl=304800"
        />
      </Head>
      <div className="bg-ash-100">
        <NavbarLegacy />
        <div className="pt-20 max-w-5xl mx-auto px-4">
          <section
            className={cn(
              "md:py-20 py-12 flex flex-col  justify-center items-center gap-6",
              jakartaSans.variable,
              clashGrotesk.variable
            )}
          >
            <h1
              className="font-clash font-medium text-5xl md:text-7xl  leading-[0.95] tracking-snug text-center relative"
              itemProp="headline"
            >
              Rebolt is trusted by hundreds of home service pros
            </h1>
            <p className=" md:text-xl text-center max-w-[760px]">
              Learn more about how home service pros are using Rebolt to grow their business, get more leads, and save
              time. See the real numbers from real home service pros — before and after using Rebolt.
            </p>
          </section>

          <div className="flex flex-col gap-10 mb-12 ">
            {caseStudies.map((caseStudy) => (
              <Link key={caseStudy.slug} href={`/case-studies/${caseStudy.slug}`} className="group">
                <div className="bg-white rounded-3xl flex-col lg:flex-row flex justify-between gap-6 w-full h-full lg:min-h-[400px]">
                  <div className="flex flex-col gap-5 h-96 lg:h-auto">
                    <div className="relative rounded-3xl overflow-hidden w-full lg:w-96 h-full">
                      <Image
                        src={
                          formatImageUrl(
                            caseStudy.heroImage?.fields.file.url ||
                              caseStudy.testimonialCtaImage?.fields.file.url ||
                              caseStudy.whatsWorkingImage?.fields.file.url ||
                              ""
                          ) || "/rebolt-branding/rebolt-app-icon.png"
                        }
                        alt={caseStudy.title}
                        fill
                        sizes="(max-width: 768px) 90vw, 45vw"
                        className="rounded-3xl w-full lg:w-96 object-cover group-hover:scale-105 transition-all duration-300 h-full"
                        loading="eager"
                        priority
                      />
                      <div className="absolute top-0 left-0 right-0 bottom-0 bg-linear-to-t from-ash-900/80 to-ash-900/0 rounded-3xl flex flex-col items-start justify-end p-6"></div>
                    </div>
                  </div>
                  <div className="px-6 flex flex-col justify-between gap-4 p-8 pr-10">
                    <h3 className="font-clash font-semibold text-3xl tracking-snug relative ">{caseStudy.title}</h3>
                    <div className="flex flex-col gap-6">
                      <div className="pb-6 border-b border-ash-200 flex flex-col gap-8">
                        <div className="flex flex-row flex-wrap gap-5">
                          {caseStudy.generalReboltStat.map((stat: HeaderSubheaderComponent, index: number) => (
                            <div key={index} className="flex flex-col">
                              <p className="text-lg font-clash font-semibold text-ash-900">{stat.fields.header}</p>
                              <p className="text-sm text-ash-700">{stat.fields.subheader}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex xs:items-center justify-between gap-8 flex-col items-start xs:flex-row">
                        <div className="flex flex-col gap-1">
                          <p className="text-ash-800 font-clash font-medium text-sm">{caseStudy.ownerName}</p>
                          <p className="text-ash-600 font-medium text-xs">Owner of {caseStudy.companyName}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className=" font-medium text-rebolt-500">Read full story</p>
                          <ArrowRightIcon className="w-4 h-4 text-rebolt-500 " />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default CaseStudies;

export const getStaticProps = async () => {
  const { pages } = await getAllPages(1, 15, "pageCaseStudy", true);

  return {
    props: {
      caseStudies: pages,
    },
  };
};
